import { Table, Button, Modal, Input, Tag, message, Badge, Row, Col, Typography, Timeline } from "antd";
import { useState, useContext, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

export default function GetStatusChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const [statusChanges, setStatusChanges] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [activeStatus, setActiveStatus] = useState("PENDING");
    const [searchText, setSearchText] = useState("");
    const [counts, setCounts] = useState({ pending_count: 0, approved_count: 0, rejected_count: 0 });
    const [loading, setLoading] = useState(false);

    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);

    const isSuperuser = authData?.employee?.is_superuser;
    const isHRViewOnly = authData?.employee?.role === 2;
    const canAction = isSuperuser && !isHRViewOnly;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchCounts = useCallback(async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/employee-status-change-counts/`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            setCounts(res.data);
        } catch (error) {
            console.error("Error fetching counts:", error);
        }
    }, [authData, backUrl]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("approval_status", activeStatus);
            if (searchText.trim()) params.append("search", searchText.trim());

            const res = await axios.get(`${backUrl}/api/emp/employee-status-change/?${params.toString()}`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            setStatusChanges(res.data.results || res.data || []);
        } catch (error) {
            console.error("Error fetching status changes:", error.response || error.message);
        } finally {
            setLoading(false);
        }
    }, [authData, backUrl, activeStatus, searchText]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    const statusColors = {
        Pending: "orange", Approved: "green", Rejected: "red",
        Cancelled: "gray", RESIGNED: "volcano", TERMINATED: "red",
        ABSCONDED: "magenta",
    };

    const handleApprove = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/${selectedRecord.id}/approve/`,
                { admin_remarks: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "Status change approved successfully.");
            setIsApproveModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
            await fetchCounts();
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to approve status change.");
        }
    };

    const handleReject = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/${selectedRecord.id}/reject/`,
                { admin_remarks: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "Status change rejected successfully.");
            setIsRejectModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
            await fetchCounts();
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to reject status change.");
        }
    };

    const statusButtons = [
        { label: "PENDING", color: "#faad14", activeColor: "#faad14", count: counts.pending_count },
        { label: "APPROVED", color: "#4CAF50", activeColor: "#4CAF50", count: counts.approved_count },
        { label: "REJECTED", color: "#f5222d", activeColor: "#f5222d", count: counts.rejected_count },
    ];

    const baseColumns = [
        {
            title: "Employee Code",
            dataIndex: ["employee_details", "user", "username"],
            key: "employee_code",
            render: (text) => text || "-",
        },
        {
            title: "Employee",
            key: "employee",
            render: (_, record) => {
                const user = record?.employee_details?.user;
                return user ? `${user.first_name} ${user.last_name}` : "-";
            }
        },
        {
            title: "Requested Status",
            dataIndex: "requested_status",
            key: "requested_status",
            render: (text) => <Tag color={statusColors[text] || "blue"}>{text || "-"}</Tag>,
        },
        {
            title: "Date of Leaving",
            dataIndex: "date_of_leaving",
            key: "date_of_leaving",
            render: (date) => date ? dayjs(date).format("DD/MM/YYYY") : "-",
        },
        {
            title: "Last Working Day",
            dataIndex: "last_working_day",
            key: "last_working_day",
            render: (date) => date ? dayjs(date).format("DD/MM/YYYY") : "-",
        },
        {
            title: "Remark",
            dataIndex: "remarks",
            key: "remarks",
            width: 200,
            render: (text) => <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>{text || "-"}</div>,
        },
        ...(activeStatus !== "PENDING" ? [{
            title: "Approver Remarks",
            dataIndex: "admin_remarks",
            key: "admin_remarks",
            width: 200,
            render: (text) => <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>{text || "-"}</div>,
        }] : []),
        {
            title: "Approval Status",
            dataIndex: "approval_status",
            key: "approval_status",
            render: (status) => (
                <Tag color={status === "APPROVED" ? "green" : status === "REJECTED" ? "red" : "orange"}>
                    {status || "PENDING"}
                </Tag>
            ),
        },
    ];

    // Superuser gets action buttons only in PENDING or ALL tabs for items that are PENDING
    const columns = canAction 
        ? [
            ...baseColumns,
            {
                title: "Actions",
                key: "actions",
                render: (_, record) => (
                    record.approval_status === "PENDING" ? (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button type="text" style={{ color: "green" }} icon={<CheckOutlined />}
                                onClick={() => { setSelectedRecord(record); setIsApproveModalVisible(true); }} />
                            <Button type="text" danger icon={<CloseOutlined />}
                                onClick={() => { setSelectedRecord(record); setIsRejectModalVisible(true); }} />
                        </div>
                    ) : null
                ),
            },
        ]
        : baseColumns;

    return (
        <>
            {/* Approve Modal */}
            <Modal title="Approve Status Change" open={isApproveModalVisible}
                onOk={handleApprove} onCancel={() => { setIsApproveModalVisible(false); setAdminRemarks(""); setSelectedRecord(null); }}
                okText="Approve" cancelText="Close"
                okButtonProps={{ style: { backgroundColor: "green", borderColor: "green" } }}>
                <p>Are you sure you want to <strong>approve</strong> this request{selectedRecord?.id ? ` (ID: ${selectedRecord.id})` : ""}?</p>
                <Input.TextArea rows={4} placeholder="Enter admin remarks" value={adminRemarks} onChange={(e) => setAdminRemarks(e.target.value)} />
            </Modal>

            {/* Reject Modal */}
            <Modal title="Reject Status Change" open={isRejectModalVisible}
                onOk={handleReject} onCancel={() => { setIsRejectModalVisible(false); setAdminRemarks(""); setSelectedRecord(null); }}
                okText="Reject" cancelText="Close" okButtonProps={{ danger: true }}>
                <p>Are you sure you want to <strong>reject</strong> this request{selectedRecord?.id ? ` (ID: ${selectedRecord.id})` : ""}?</p>
                <Input.TextArea rows={4} placeholder="Enter rejection remarks" value={adminRemarks} onChange={(e) => setAdminRemarks(e.target.value)} />
            </Modal>

            {/* Header */}
            <Title level={4} style={{ 
                color: "#0e497b", 
                marginBottom: 16, 
                textAlign: "center",
                textTransform: "uppercase" 
            }}>
                Employee Status Change Requests
            </Title>



            {/* Status Tab Buttons */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                {statusButtons.map((btn) => {
                    const isActive = activeStatus === btn.label;
                    return (
                        <Button
                            key={btn.label}
                            type={isActive ? "primary" : "default"}
                            onClick={() => setActiveStatus(btn.label)}
                            style={{
                                marginRight: "10px",
                                marginBottom: isMobile ? "10px" : "0",
                                backgroundColor: isActive ? btn.activeColor : undefined,
                                borderColor: isActive ? btn.activeColor : undefined,
                                color: isActive ? "#fff" : undefined,
                                minWidth: "120px"
                            }}
                        >
                            {btn.label} ({btn.count || 0})
                        </Button>
                    );
                })}
            </div>

            {/* Filter Section (Search) */}
            <div
                style={{
                    background: "#ffffff",
                    padding: "15px",
                    borderRadius: "10px",
                    marginBottom: "15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                }}
            >
                <Row gutter={[16, 16]} justify="center">
                    <Col xs={24} sm={18} md={10}>
                        <Input
                            placeholder="Employee Name / Code"
                            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            style={{ width: "100%" }}
                        />
                    </Col>
                </Row>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={statusChanges.map((item, index) => ({ key: index, ...item }))}
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{ backgroundColor: "#0e497b", color: "white", whiteSpace: "nowrap" }}>
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}